/**
 * What this browser keeps of documents (the account's This device page): the copies kept as they're edited, to get
 * back what a server lost (office/sync/recovery.ts), seen, saved as new documents (beside their document when it's
 * there; else on their own, private to you, their document gone) or removed; changes the server doesn't have yet
 * (office/sync/offline.ts), saved as documents or removed; drafts of documents edited without live editing
 * (office/draft.ts), removed. Each a list as the others are: a page at a time, each row its document's title (found
 * from its id: `/links/{id}`, then the item), its icon and when, rows selected to act on together. And how much it all
 * takes, as the browser counts it.
 */

import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { ApiError, api } from "../api/client.ts";
import type { ItemDetail } from "../api/types.gen.ts";
import { locale, t } from "../i18n/index.ts";
import { DOCUMENT_MIME, SPREADSHEET_MIME } from "../lib/docMimes.ts";
import { showDark } from "../lib/documents.ts";
import { formatBytes, formatInstant, formatRelative } from "../lib/format.ts";
import { ignore } from "../lib/ignore.ts";
import { lazy } from "../lib/lazy.ts";
import { spaceUrl } from "../lib/paths.ts";
import { asApiError } from "../lib/resource.ts";
import { navigate } from "../lib/router.ts";
import { SELECTED_ROW, useSelection } from "../lib/selection.ts";
import { me } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { type DocumentsModule, documentsChunk } from "../office/documentsChunk.ts";
import { allDrafts, idbStore } from "../office/draft.ts";
import { allKept, liveStore } from "../office/sync/offline.ts";
import { type Copy, recoveryStore } from "../office/sync/recovery.ts";
import { Button, Link } from "../ui/Button.tsx";
import { ConfirmDialog, Dialog } from "../ui/Dialog.tsx";
import { Section } from "../ui/misc.tsx";
import { SelectBox, SelectionRow } from "../ui/selection.tsx";
import { LINK } from "../ui/styles.ts";
import { AccountTabs } from "./account/tabs.tsx";
import { KindIcon } from "./items/icons.tsx";

/** What reads a live document's state kept here (the editors' readers: a chunk of their own, loaded when needed). */
const reader = lazy(() => import("../office/sync/useRecovery.ts"));

/** Rows a page. */
const PAGE = 10;
const ITEM_ID = /^itm_[a-z0-9]+$/u;

let chunk: DocumentsModule | null = null;

function useChunk(): DocumentsModule | null {
  const [mod, setMod] = useState<DocumentsModule | null>(chunk);
  useEffect(() => {
    if (!chunk) {
      documentsChunk.load().then((m) => {
        chunk = m;
        setMod(m);
      }, ignore);
    }
  }, []);
  return mod;
}

/** A document, as found from its id: its title, type and space; gone (or not yours); one of the documents lab's. */
type Found = { title: string; mime?: string | undefined; space: string } | "gone" | "lab";

const found = new Map<string, Promise<Found>>();

/** What document `id` is (asked once, kept for the visit). */
function find(id: string): Promise<Found> {
  if (!ITEM_ID.test(id)) {
    return Promise.resolve("lab");
  }
  let f = found.get(id);
  if (!f) {
    f = api
      .get<{ space: string }>(`/links/${id}`)
      .then(async ({ space }) => {
        const item = await api.get<ItemDetail>(`/spaces/${space}/items/${id}`);
        return { title: item.title ?? "", mime: item.file?.mime, space };
      })
      .catch((): Found => "gone");
    found.set(id, f);
  }
  return f;
}

/** What the documents `ids` are, as they're found. */
function useFound(ids: readonly string[]): ReadonlyMap<string, Found> {
  const [known, setKnown] = useState<ReadonlyMap<string, Found>>(new Map());
  const key = ids.join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` is the ids.
  useEffect(() => {
    let gone = false;
    for (const id of new Set(ids)) {
      find(id)
        .then((f) => !gone && setKnown((was) => new Map(was).set(id, f)))
        .catch(ignore);
    }
    return () => {
      gone = true;
    };
  }, [key]);
  return known;
}

/** A space of yours to save in: the one given, else your first. */
const spaceFor = (space: string | undefined): string | null =>
  space ?? me.value?.orgs.flatMap((o) => o.spaces.map((s) => s.space))[0] ?? null;

interface Saved {
  item: string;
  kind: "doc" | "sheet";
  content: string;
  title?: string | undefined;
  space?: string | undefined;
}

/**
 * Saves `c` (a document's content, of `kind`) as a recovery copy of `c.item`: beside it when it's there (its
 * visibility), else a document of its own, private to you (its document gone, or not yours to see). Its new id and space.
 */
async function saveAsNew(c: Saved): Promise<{ id: string; space: string }> {
  const space = spaceFor(c.space);
  if (!space) {
    throw new Error(t("device.noSpace"));
  }
  const content = JSON.parse(c.content) as unknown;
  try {
    const made = await api.post<ItemDetail>(`/spaces/${space}/items/${c.item}/copies`, { content, recovered: true });
    return { id: made.id, space };
  } catch (e) {
    if (!(e instanceof ApiError && (e.status === 404 || e.status === 403))) {
      throw e;
    }
  }
  const made = await api.post<ItemDetail>(`/spaces/${space}/files/new`, {
    kind: c.kind,
    title: t("device.recoveredTitle", { title: c.title ?? t("device.untitled") }),
    content,
    recovered_from: c.item,
    private: true,
  });
  return { id: made.id, space };
}

/** Saves them all, one after another: one opened; several, said how many. */
async function saveAll(all: readonly Saved[]): Promise<void> {
  const made: { id: string; space: string }[] = [];
  for (const c of all) {
    // biome-ignore lint/performance/noAwaitInLoops: one at a time, as the server takes them.
    made.push(await saveAsNew(c));
  }
  const [only] = made;
  if (made.length === 1 && only) {
    navigate(spaceUrl({ id: only.space }, `/items/${only.id}`));
  } else {
    toast(t("device.savedMany", { count: made.length }));
  }
}

/** One row: what's kept, of document `item`. */
interface Entry {
  key: string;
  item: string;
  at: number;
  kind?: "doc" | "sheet" | undefined;
  /** Its document's title then (a copy's). */
  title?: string | undefined;
  /** Beside its name: a badge, its size. */
  extra?: ComponentChildren;
  /** At its end: what's done to it alone (View). */
  own?: ComponentChildren;
}

const when = (at: number) => {
  const iso = new Date(at).toISOString();
  return (
    <time dateTime={iso} title={formatInstant(iso)} class="shrink-0 text-zinc-500">
      {formatRelative(iso)}
    </time>
  );
};

const mimeOf = (kind: "doc" | "sheet" | undefined) =>
  kind === "sheet" ? SPREADSHEET_MIME : kind === "doc" ? DOCUMENT_MIME : undefined;

/** Its document's name: its title, linked (found); gone; the lab's; its id meanwhile. */
function Name({ e, f }: { e: Entry; f: Found | undefined }) {
  if (f === "lab") {
    return <span class="text-zinc-600">{t("device.lab", { id: e.item })}</span>;
  }
  const title = (typeof f === "object" ? f.title : e.title) || t("device.untitled");
  return (
    <span class="flex min-w-0 items-center gap-2">
      {typeof f === "object" ? (
        <Link href={spaceUrl({ id: f.space }, `/items/${e.item}`)} class={`${LINK} truncate`}>
          {title}
        </Link>
      ) : (
        <span class="truncate text-zinc-900">{title}</span>
      )}
      {f === "gone" ? <span class="shrink-0 text-xs text-zinc-500">{t("device.gone")}</span> : null}
    </span>
  );
}

/**
 * A section's list: a page at a time ("Show more"), each row's checkbox, its icon, its document's name and when; those
 * selected saved as new documents (`save`) or removed (`remove`, asked first).
 */
function List(p: {
  name: string;
  entries: readonly Entry[];
  save?: ((keys: readonly string[]) => Promise<void>) | undefined;
  remove: (keys: readonly string[]) => Promise<void>;
  removeMessage: string;
}) {
  const [shown, setShown] = useState(PAGE);
  const page = p.entries.slice(0, shown);
  const sel = useSelection(`device:${p.name}`);
  const known = useFound(page.map((e) => e.item));
  const [busy, setBusy] = useState(false);
  const run = (what: () => Promise<void>) => {
    setBusy(true);
    what()
      .then(() => sel.setAll(sel.ids, false))
      .catch((e: unknown) => toast(asApiError(e).message, "error"))
      .finally(() => setBusy(false));
  };
  const picked = () => [...sel.ids];
  const [confirming, setConfirming] = useState(false);
  const save = () => run(() => p.save?.(picked()) ?? Promise.resolve());
  return (
    <>
      <SelectionRow sel={sel} shown={{ ids: page.map((e) => e.key), total: page.length, filtered: false }}>
        {(bar) =>
          bar([
            // (Each into "⋯" as the bar narrows, as the other lists' are, Remove first.)
            ...(p.save
              ? [
                  {
                    key: "save",
                    node: (
                      <Button size="sm" busy={busy} data-action="deviceSave" onClick={save}>
                        {t("office.versions.saveShort")}
                      </Button>
                    ),
                    drop: 20,
                    menu: [{ label: t("office.versions.saveShort"), onSelect: save }],
                  },
                ]
              : []),
            {
              key: "remove",
              node: (
                <Button size="sm" busy={busy} onClick={() => setConfirming(true)}>
                  {t("device.letGo")}
                </Button>
              ),
              drop: 30,
              menu: [{ label: t("device.letGo"), danger: true, onSelect: () => setConfirming(true) }],
            },
          ])
        }
      </SelectionRow>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title={t("device.letGoTitle")}
        message={p.removeMessage}
        confirmLabel={t("device.letGo")}
        onConfirm={() => run(() => p.remove(picked()))}
      />
      <ul class="divide-y divide-zinc-200 rounded-lg border border-zinc-200 bg-white">
        {page.map((e) => {
          const f = known.get(e.item);
          const mime = (typeof f === "object" ? f.mime : undefined) ?? mimeOf(e.kind);
          return (
            <li
              key={e.key}
              class={`flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 text-sm ${sel.has(e.key) ? SELECTED_ROW : ""}`}
              data-device-row={e.key}
            >
              <SelectBox sel={sel} id={e.key} title={typeof f === "object" ? f.title : e.item} />
              <KindIcon
                item={{ kind: "file", file: mime ? { mime, sha256: "", size: 0 } : null, title: "" } as never}
              />
              <span class="min-w-0 flex-1">
                <Name e={e} f={f} />
              </span>
              {e.extra}
              {when(e.at)}
              {e.own}
            </li>
          );
        })}
      </ul>
      {p.entries.length > shown ? (
        <button type="button" class={`${LINK} mt-2 text-sm`} onClick={() => setShown(shown + PAGE)}>
          {t("device.more", { count: p.entries.length - shown })}
        </button>
      ) : null}
    </>
  );
}

/** A copy seen, read-only, in a dialog. */
function Preview({ copy, onClose }: { copy: Copy; onClose: () => void }) {
  const mod = useChunk();
  // Its pictures' links: its document's, when it's there (a copy keeps their names, not their links).
  const [media, setMedia] = useState<Readonly<Record<string, string>>>({});
  useEffect(() => {
    find(copy.item)
      .then(async (f) => {
        if (typeof f === "object") {
          const d = await api.get<{ media: Record<string, string> }>(`/spaces/${f.space}/items/${copy.item}/document`);
          setMedia(d.media);
        }
      })
      .catch(ignore);
  }, [copy.item]);
  return (
    <Dialog open={true} onClose={onClose} title={copy.title ?? t("device.untitled")} wide={true}>
      <div class="h-[70dvh] p-2">
        {mod ? (
          <mod.ReadOnlyDocument
            content={JSON.parse(copy.content) as never}
            media={media}
            locale={locale.value}
            dark={showDark()}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

function Copies() {
  const [copies, setCopies] = useState<Copy[] | null>(null);
  const [seen, setSeen] = useState<Copy | null>(null);
  const load = () => {
    recoveryStore()
      .all()
      .then(setCopies)
      .catch(() => setCopies([]));
  };
  useEffect(load, []);
  if (copies === null) {
    return null;
  }
  const byKey = new Map(copies.map((c) => [c.id, c]));
  return (
    <Section title={t("device.copies")}>
      <p class="mb-3 text-sm text-zinc-600">{t("device.copiesHint")}</p>
      {copies.length === 0 ? (
        <p class="text-sm text-zinc-500">{t("device.none")}</p>
      ) : (
        <List
          name="copies"
          entries={copies.map((c) => ({
            key: c.id,
            item: c.item,
            at: c.at,
            kind: c.kind,
            title: c.title,
            extra: (
              <>
                {c.lost ? (
                  <span class="shrink-0 rounded bg-red-100 px-1.5 py-px text-xs font-medium text-red-800">
                    {t("office.versions.lost")}
                  </span>
                ) : null}
                <span class="shrink-0 text-xs text-zinc-500">{formatBytes(c.content.length)}</span>
              </>
            ),
            own: (
              <Button size="sm" onClick={() => setSeen(c)} data-action="deviceView">
                {t("device.see")}
              </Button>
            ),
          }))}
          save={(keys) => saveAll(keys.flatMap((k) => byKey.get(k) ?? []))}
          remove={async (keys) => {
            await Promise.all(keys.map((k) => recoveryStore().delete(k)));
            load();
          }}
          removeMessage={t("device.letGoCopy")}
        />
      )}
      {seen ? <Preview copy={seen} onClose={() => setSeen(null)} /> : null}
    </Section>
  );
}

function Pending() {
  const [read, setRead] = useState<Awaited<ReturnType<typeof reader>> | null>(null);
  const [kept, setKept] = useState<Awaited<ReturnType<typeof allKept>> | null>(null);
  const load = () => {
    allKept()
      .then(setKept)
      .catch(() => setKept([]));
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: read once, as the page opens.
  useEffect(() => {
    load();
    reader().then(setRead, ignore);
  }, []);
  if (!kept?.length) {
    return null;
  }
  const contents = new Map(kept.map((k) => [k.id, read?.contentOfState(k.state) ?? null]));
  return (
    <Section title={t("device.pending")}>
      <p class="mb-3 text-sm text-zinc-600">{t("device.pendingHint")}</p>
      <List
        name="pending"
        entries={kept.map((k) => ({ key: k.id, item: k.id, at: k.updated, kind: contents.get(k.id)?.kind }))}
        save={(keys) =>
          saveAll(
            keys.flatMap((k) => {
              const c = contents.get(k);
              return c ? [{ item: k, ...c }] : [];
            }),
          )
        }
        remove={async (keys) => {
          await Promise.all(keys.map((k) => liveStore().delete(k)));
          load();
        }}
        removeMessage={t("device.letGoPending")}
      />
    </Section>
  );
}

function Drafts() {
  const [drafts, setDrafts] = useState<Awaited<ReturnType<typeof allDrafts>> | null>(null);
  const load = () => {
    allDrafts()
      .then(async (ds) => {
        // One with no changes keeps nothing: let go (as an editor now does when its changes are all saved).
        await Promise.allSettled(ds.filter((x) => x.changes.length === 0).map((d) => idbStore<unknown>().delete(d.id)));
        setDrafts(ds.filter((d) => d.changes.length > 0).sort((a, b) => b.updated - a.updated));
      })
      .catch(() => setDrafts([]));
  };
  useEffect(load, []);
  if (!drafts?.length) {
    return null;
  }
  return (
    <Section title={t("device.drafts")}>
      <p class="mb-3 text-sm text-zinc-600">{t("device.draftsHint")}</p>
      <List
        name="drafts"
        entries={drafts.map((d) => ({
          key: d.id,
          item: d.id,
          at: d.updated,
          extra: <span class="shrink-0 text-xs text-zinc-500">{t("device.changes", { count: d.changes.length })}</span>,
        }))}
        remove={async (keys) => {
          await Promise.all(keys.map((k) => idbStore<unknown>().delete(k)));
          load();
        }}
        removeMessage={t("device.letGoDraft")}
      />
    </Section>
  );
}

/** How much this browser keeps for the app, as it counts it. */
function Usage() {
  const [used, setUsed] = useState<number | null>(null);
  useEffect(() => {
    navigator.storage
      ?.estimate()
      .then((e) => setUsed(e.usage ?? null))
      .catch(ignore);
  }, []);
  return used === null ? null : (
    <p class="mb-6 text-sm text-zinc-600">{t("device.usage", { size: formatBytes(used) })}</p>
  );
}

export function DeviceData() {
  return (
    <>
      <AccountTabs current="/profile/device" />
      <p class="mb-2 text-sm text-zinc-600">{t("device.hint")}</p>
      <Usage />
      <Copies />
      <Pending />
      <Drafts />
    </>
  );
}
